import React, { useEffect, useRef, useState } from "react";
import { api, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { Palette, Image as ImageIcon, Upload, Loader2, X, Smartphone, Download, Sparkles, Check, CreditCard, KeyRound, Eye, RefreshCw } from "lucide-react";
import { useBranding } from "../../context/BrandingContext";
import { THEME_PRESETS, DEFAULT_PRESET, getPreset, getPresetDefaultColors } from "../../theme/presets";
import { resolveTenantSlug } from "../../lib/api";
import HeroLivePreview from "../../components/admin/HeroLivePreview";

export default function AdminBranding() {
  const { branding: current, refresh } = useBranding();
  const [form, setForm] = useState({
    brand_name: "",
    short_name: "",
    tagline: "",
    logo_square_url: "",
    logo_horizontal_url: "",
    // Storefront colour overrides — empty string means "no override; use preset
    // default". Pre-populated from the active preset on first load so the
    // ColorField inputs show what the storefront is currently rendering.
    primary_color: "",
    accent_color: "",
    background_color: "",
    surface_color: "",
    theme_color: "",   // PWA chrome override (Android address bar)
    theme_preset: DEFAULT_PRESET,
    font_pair: "",
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_webhook_secret: "",
    // Homepage Hero overrides
    hero_image_url: "",
    hero_eyebrow: "",
    hero_title: "",
    hero_subtitle: "",
    hero_cta_primary_label: "",
    hero_cta_primary_link: "",
    hero_cta_secondary_label: "",
    hero_cta_secondary_link: "",
  });
  const [razorpaySecretSet, setRazorpaySecretSet] = useState(false);
  const [razorpayWebhookSet, setRazorpayWebhookSet] = useState(false);
  const [savingPayments, setSavingPayments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const tenantSlug = resolveTenantSlug();
  const previewSrc = `/t/${tenantSlug}/?preview=${previewKey}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/branding");
        setForm({
          brand_name: data.brand_name || "",
          short_name: data.short_name || "",
          tagline: data.tagline || "",
          logo_square_url: data.logo_square_url || "",
          logo_horizontal_url: data.logo_horizontal_url || "",
          // Colour fields: empty string when no override is saved, so the
          // ColorField's placeholder + "preset" badge surfaces. The actual
          // colour swatch shown in the picker comes from the preset
          // default (see presetColors below).
          primary_color: data.primary_color || "",
          accent_color: data.accent_color || "",
          background_color: data.background_color || "",
          surface_color: data.surface_color || "",
          theme_color: data.theme_color || "",
          theme_preset: data.theme_preset || DEFAULT_PRESET,
          font_pair: data.font_pair || "",
          razorpay_key_id: data.razorpay_key_id || "",
          razorpay_key_secret: "",
          razorpay_webhook_secret: "",
          hero_image_url: data.hero_image_url || "",
          hero_eyebrow: data.hero_eyebrow || "",
          hero_title: data.hero_title || "",
          hero_subtitle: data.hero_subtitle || "",
          hero_cta_primary_label: data.hero_cta_primary_label || "",
          hero_cta_primary_link: data.hero_cta_primary_link || "",
          hero_cta_secondary_label: data.hero_cta_secondary_label || "",
          hero_cta_secondary_link: data.hero_cta_secondary_link || "",
        });
        setRazorpaySecretSet(!!data.razorpay_key_secret_set);
        setRazorpayWebhookSet(!!data.razorpay_webhook_secret_set);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const save = async (overrides = {}) => {
    const next = { ...form, ...overrides };
    if (!next.brand_name.trim()) return toast.error("Brand name is required");
    setSaving(true);
    try {
      const payload = {
        brand_name: next.brand_name.trim(),
        short_name: next.short_name.trim() || null,
        tagline: next.tagline.trim() || null,
        logo_square_url: (next.logo_square_url || "").trim() || null,
        logo_horizontal_url: (next.logo_horizontal_url || "").trim() || null,
        // Persist `null` when the field is empty so the backend falls back
        // to the preset default (resets the override).
        primary_color: next.primary_color || null,
        accent_color: next.accent_color || null,
        background_color: next.background_color || null,
        surface_color: next.surface_color || null,
        theme_color: next.theme_color || null,
        theme_preset: next.theme_preset || DEFAULT_PRESET,
        font_pair: next.font_pair || null,
        hero_image_url: (next.hero_image_url || "").trim() || null,
        hero_eyebrow: (next.hero_eyebrow || "").trim() || null,
        hero_title: (next.hero_title || "").trim() || null,
        hero_subtitle: (next.hero_subtitle || "").trim() || null,
        hero_cta_primary_label: (next.hero_cta_primary_label || "").trim() || null,
        hero_cta_primary_link: (next.hero_cta_primary_link || "").trim() || null,
        hero_cta_secondary_label: (next.hero_cta_secondary_label || "").trim() || null,
        hero_cta_secondary_link: (next.hero_cta_secondary_link || "").trim() || null,
      };
      await api.put("/admin/branding", payload);
      toast.success("Branding updated");
      await refresh();
      // Hard-refresh the live preview iframe so the customer-facing storefront
      // re-bootstraps with the new branding/theme tokens.
      setPreviewKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const savePaymentKeys = async () => {
    setSavingPayments(true);
    try {
      const payload = {
        brand_name: form.brand_name.trim() || current?.brand_name || "Store",
        razorpay_key_id: form.razorpay_key_id.trim() || null,
      };
      if (form.razorpay_key_secret) payload.razorpay_key_secret = form.razorpay_key_secret;
      if (form.razorpay_webhook_secret) payload.razorpay_webhook_secret = form.razorpay_webhook_secret;
      await api.put("/admin/branding", payload);
      toast.success("Razorpay keys saved");
      setForm({ ...form, razorpay_key_secret: "", razorpay_webhook_secret: "" });
      setRazorpaySecretSet(!!(form.razorpay_key_secret || razorpaySecretSet));
      setRazorpayWebhookSet(!!(form.razorpay_webhook_secret || razorpayWebhookSet));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPayments(false);
    }
  };

  /**
   * Update a logo field AND immediately persist it. Called by LogoUploader
   * after both an upload (new URL) and a remove (empty string). Without
   * this auto-save, users frequently click the X expecting it to "remove"
   * the logo, then leave the page without clicking Save and the old logo
   * stays live in production.
   */
  const setLogoAndPersist = async (key, url) => {
    setForm((f) => ({ ...f, [key]: url || "" }));
    await save({ [key]: url || "" });
  };

  const exportBundle = async () => {
    setExporting(true);
    try {
      const { data, headers } = await api.get("/admin/branding/export.zip", {
        responseType: "blob",
      });
      // Parse filename from Content-Disposition (falls back to a safe default)
      const disp = headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      const filename = m ? m[1] : "branding.zip";
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bundle exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!loaded) {
    return <div className="p-8 text-center text-[hsl(var(--kb-muted))]">Loading…</div>;
  }

  return (
    <div data-testid="admin-branding-page" className="max-w-7xl">
      <div className="mb-8">
        <div className="overline mb-1">Settings</div>
        <h1 className="font-display text-3xl md:text-4xl">Branding & PWA</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-xl">
          Rebrand the storefront end-to-end. Your brand name and logo power the header, footer, emails, and the Install-to-Home-Screen (PWA) experience.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_460px] gap-6 items-start">
        <div className="space-y-6 bg-white border border-[hsl(var(--kb-border))] p-6">
        <section>
          <h2 className="font-display text-xl mb-4">Brand identity</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Brand name">
              <input
                type="text"
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                className="kb-input"
                maxLength={60}
                data-testid="branding-name"
              />
            </Field>
            <Field label="Short name (PWA, ≤12 chars)">
              <input
                type="text"
                value={form.short_name}
                onChange={(e) => setForm({ ...form, short_name: e.target.value })}
                className="kb-input"
                maxLength={12}
                placeholder="Auto-trimmed from brand name"
                data-testid="branding-short-name"
              />
            </Field>
            <Field label="Tagline" className="md:col-span-2">
              <input
                type="text"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                className="kb-input"
                maxLength={120}
                placeholder="Shown in footer and PWA description"
                data-testid="branding-tagline"
              />
            </Field>
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><ImageIcon size={18} /> Logos</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Upload a square logo (≥512×512) for the PWA/favicon, plus an optional horizontal logo for the header.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <LogoUploader
              label="Square logo"
              testid="square"
              value={form.logo_square_url}
              onChange={(url) => setLogoAndPersist("logo_square_url", url)}
              aspectClass="aspect-square"
              hint="Used as favicon + PWA install icon (512×512 ideal). Saves automatically on upload/remove."
            />
            <LogoUploader
              label="Horizontal logo (optional)"
              testid="horizontal"
              value={form.logo_horizontal_url}
              onChange={(url) => setLogoAndPersist("logo_horizontal_url", url)}
              aspectClass="aspect-[4/1]"
              hint="Replaces text in the store header when set. Saves automatically on upload/remove."
            />
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="hero-section">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><ImageIcon size={18} /> Homepage hero</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Customise the big banner customers see at the top of your storefront. Leave any field blank to keep the default for your chosen theme.
            Use <code className="bg-[hsl(var(--kb-surface-alt))] px-1 rounded">*word*</code> in the headline to italicise + accent-colour that word, and a new line for a line break.
          </p>
          <div className="grid xl:grid-cols-[1fr_220px] gap-6 items-start">
            <div>
              <div className="grid md:grid-cols-2 gap-6">
                <LogoUploader
                  label="Hero background image"
                  testid="hero-image"
                  value={form.hero_image_url}
                  onChange={(url) => setLogoAndPersist("hero_image_url", url)}
                  aspectClass="aspect-[16/9]"
                  hint="Large background image behind the headline (1920×1080 ideal). Saves automatically on upload/remove."
                />
                <div className="space-y-4">
                  <Field label="Eyebrow (small text above headline)">
                    <input
                      type="text"
                      value={form.hero_eyebrow}
                      onChange={(e) => setForm({ ...form, hero_eyebrow: e.target.value })}
                      className="kb-input"
                      maxLength={120}
                      placeholder="e.g. The Bridal Edit · Autumn–Winter"
                      data-testid="hero-eyebrow"
                    />
                  </Field>
                  <Field label="Headline">
                    <textarea
                      value={form.hero_title}
                      onChange={(e) => setForm({ ...form, hero_title: e.target.value })}
                      className="kb-input min-h-[80px] font-display"
                      maxLength={240}
                      rows={2}
                      placeholder={"e.g. Worn by stories.\nWoven *by hands*."}
                      data-testid="hero-title"
                    />
                  </Field>
                  <Field label="Subtitle (paragraph)">
                    <textarea
                      value={form.hero_subtitle}
                      onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })}
                      className="kb-input min-h-[64px]"
                      maxLength={400}
                      rows={2}
                      placeholder="One-or-two-line description shown beneath the headline."
                      data-testid="hero-subtitle"
                    />
                  </Field>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mt-6 pt-4 border-t border-dashed border-[hsl(var(--kb-border))]">
                <div className="space-y-3">
                  <div className="overline text-[10px]">Primary button</div>
                  <Field label="Button label">
                    <input
                      type="text"
                      value={form.hero_cta_primary_label}
                      onChange={(e) => setForm({ ...form, hero_cta_primary_label: e.target.value })}
                      className="kb-input"
                      maxLength={40}
                      placeholder="e.g. Explore the Bazaar"
                      data-testid="hero-cta1-label"
                    />
                  </Field>
                  <Field label="Button link">
                    <input
                      type="text"
                      value={form.hero_cta_primary_link}
                      onChange={(e) => setForm({ ...form, hero_cta_primary_link: e.target.value })}
                      className="kb-input font-mono text-xs"
                      maxLength={240}
                      placeholder="/shop"
                      data-testid="hero-cta1-link"
                    />
                  </Field>
                </div>
                <div className="space-y-3">
                  <div className="overline text-[10px]">Secondary button (optional)</div>
                  <Field label="Button label">
                    <input
                      type="text"
                      value={form.hero_cta_secondary_label}
                      onChange={(e) => setForm({ ...form, hero_cta_secondary_label: e.target.value })}
                      className="kb-input"
                      maxLength={40}
                      placeholder="e.g. Sell Your Craft (leave blank to hide)"
                      data-testid="hero-cta2-label"
                    />
                  </Field>
                  <Field label="Button link">
                    <input
                      type="text"
                      value={form.hero_cta_secondary_link}
                      onChange={(e) => setForm({ ...form, hero_cta_secondary_link: e.target.value })}
                      className="kb-input font-mono text-xs"
                      maxLength={240}
                      placeholder="/seller/onboard"
                      data-testid="hero-cta2-link"
                    />
                  </Field>
                </div>
              </div>
            </div>
            <div className="xl:sticky xl:top-4">
              <HeroLivePreview
                form={form}
                accentColor={form.accent_color || getPresetDefaultColors(form.theme_preset).accent_color}
              />
            </div>
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]">
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Pick a preset and your customers' homepage, header, buttons and typography swap instantly. The custom colours below act as <em>overrides</em> on top of the chosen preset.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="theme-preset-grid">
            {Object.entries(THEME_PRESETS).map(([key, p]) => {
              const active = form.theme_preset === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setForm({ ...form, theme_preset: key, font_pair: "" }); save({ theme_preset: key, font_pair: null }); }}
                  className={`text-left border p-4 transition relative ${active ? "border-[hsl(var(--kb-primary))] ring-2 ring-[hsl(var(--kb-primary))]/20" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40"}`}
                  data-testid={`theme-preset-${key}`}
                >
                  {active && (
                    <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[hsl(var(--kb-primary))] text-white flex items-center justify-center" data-testid={`theme-preset-${key}-active`}>
                      <Check size={12} />
                    </span>
                  )}
                  <div className="flex items-center gap-1 mb-3">
                    {p.swatches.map((c, i) => (
                      <span key={i} className="w-6 h-6 rounded-full border border-black/10" style={{ background: c }} />
                    ))}
                  </div>
                  <div className="font-medium">{p.label}</div>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1 leading-relaxed">{p.description}</p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="notification-sound-section">
          <NotificationSoundUploader />
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="typography-section">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Sparkles size={18} /> Typography</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Four hand-picked font pairings tuned for the <b>{getPreset(form.theme_preset).label}</b> theme.
            The first one is our recommendation — click any card to switch instantly.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="font-pair-grid">
            {(getPreset(form.theme_preset).fontPairs || []).map((pair, idx) => {
              const isActive = (form.font_pair || (getPreset(form.theme_preset).fontPairs?.[0]?.key)) === pair.key;
              return (
                <button
                  key={pair.key}
                  type="button"
                  onClick={() => { setForm({ ...form, font_pair: pair.key }); save({ font_pair: pair.key }); }}
                  className={`text-left border p-4 transition relative ${isActive ? "border-[hsl(var(--kb-primary))] ring-2 ring-[hsl(var(--kb-primary))]/20" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40"}`}
                  data-testid={`font-pair-${pair.key}`}
                >
                  {isActive && (
                    <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[hsl(var(--kb-primary))] text-white flex items-center justify-center" data-testid={`font-pair-${pair.key}-active`}>
                      <Check size={12} />
                    </span>
                  )}
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl leading-none" style={{ fontFamily: pair.display }}>Aa</span>
                    <span className="text-base leading-none text-[hsl(var(--kb-muted))]" style={{ fontFamily: pair.body }}>aa</span>
                  </div>
                  <div className="font-medium text-sm">{pair.label}{idx === 0 && <span className="ml-2 text-[10px] uppercase tracking-wider text-[hsl(var(--kb-primary))]">Recommended</span>}</div>
                  <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1 leading-snug">{pair.note}</p>
                  <div className="mt-3 text-[10px] text-[hsl(var(--kb-muted))] font-mono break-all">
                    {pair.display.split(",")[0].replace(/['"]/g, "")} / {pair.body.split(",")[0].replace(/['"]/g, "")}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="branding-colors-section">
          {(() => {
            const presetColors = getPresetDefaultColors(form.theme_preset);
            const anyOverride =
              form.primary_color || form.accent_color
              || form.background_color || form.surface_color
              || form.theme_color;
            return (
              <>
                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                  <h2 className="font-display text-xl flex items-center gap-2"><Palette size={18} /> Colours</h2>
                  {anyOverride && (
                    <button
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        primary_color: "", accent_color: "",
                        background_color: "", surface_color: "",
                        theme_color: "",
                      })}
                      className="text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] inline-flex items-center gap-1.5"
                      data-testid="branding-colors-reset-all"
                    >
                      <RefreshCw size={12} /> Reset all to preset defaults
                    </button>
                  )}
                </div>
                <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
                  These colours override the <b>{getPreset(form.theme_preset).label}</b> preset on your customer-facing storefront.
                  Empty fields fall back to the preset's defaults.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ColorField
                    label="Primary"
                    value={form.primary_color}
                    presetDefault={presetColors.primary_color}
                    hint="Buttons, links"
                    onChange={(v) => setForm({ ...form, primary_color: v })}
                    testid="primary"
                  />
                  <ColorField
                    label="Accent"
                    value={form.accent_color}
                    presetDefault={presetColors.accent_color}
                    hint="Highlights, badges"
                    onChange={(v) => setForm({ ...form, accent_color: v })}
                    testid="accent"
                  />
                  <ColorField
                    label="Background"
                    value={form.background_color}
                    presetDefault={presetColors.background_color}
                    hint="Page background"
                    onChange={(v) => setForm({ ...form, background_color: v })}
                    testid="bg"
                  />
                  <ColorField
                    label="Surface"
                    value={form.surface_color}
                    presetDefault={presetColors.surface_color}
                    hint="Cards, panels"
                    onChange={(v) => setForm({ ...form, surface_color: v })}
                    testid="surface"
                  />
                </div>
                <div className="mt-6 pt-5 border-t border-dashed border-[hsl(var(--kb-border))]">
                  <h3 className="text-sm font-medium mb-1 flex items-center gap-2">
                    <Smartphone size={14} /> PWA chrome
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mb-3">
                    Controls the Android Chrome address-bar tint when customers install your store as an app.
                    Leave blank to inherit from Primary.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ColorField
                      label="PWA theme colour"
                      value={form.theme_color}
                      presetDefault={form.primary_color || presetColors.primary_color}
                      hint="Android address bar"
                      onChange={(v) => setForm({ ...form, theme_color: v })}
                      testid="theme"
                    />
                  </div>
                </div>
              </>
            );
          })()}
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="razorpay-section">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><CreditCard size={18} /> Payments — Razorpay</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Enter your own Razorpay credentials so customer payments land directly in your Razorpay account.
            Leave blank to use the platform's mock gateway during demo / preview.
            <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="ml-1 underline text-[hsl(var(--kb-primary))]">Get your keys</a>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-xs flex flex-col">
              <span className="flex items-center gap-1 mb-1"><KeyRound size={11} /> Key ID</span>
              <input
                className="kb-input"
                value={form.razorpay_key_id}
                onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })}
                placeholder="rzp_test_xxxxxxxxxxxx or rzp_live_xxxx"
                data-testid="razorpay-key-id-input"
              />
            </label>
            <label className="text-xs flex flex-col">
              <span className="flex items-center gap-1 mb-1">
                <KeyRound size={11} /> Key Secret
                {razorpaySecretSet && !form.razorpay_key_secret && (
                  <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-testid="razorpay-secret-status">
                    saved · enter to update
                  </span>
                )}
              </span>
              <input
                type="password"
                className="kb-input"
                value={form.razorpay_key_secret}
                onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })}
                placeholder={razorpaySecretSet ? "•••••••••• (configured)" : "••••••••••••••••"}
                data-testid="razorpay-key-secret-input"
              />
            </label>
          </div>
          <label className="text-xs flex flex-col mt-3">
            <span className="flex items-center gap-1 mb-1">
              <KeyRound size={11} /> Webhook Secret <span className="text-[10px] text-[hsl(var(--kb-muted))] normal-case">(optional — for refunds &amp; dispute reconciliation)</span>
              {razorpayWebhookSet && !form.razorpay_webhook_secret && (
                <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-testid="razorpay-webhook-status">
                  saved
                </span>
              )}
            </span>
            <input
              type="password"
              className="kb-input"
              value={form.razorpay_webhook_secret}
              onChange={(e) => setForm({ ...form, razorpay_webhook_secret: e.target.value })}
              placeholder={razorpayWebhookSet ? "•••••••••• (configured)" : "Paste from Razorpay Dashboard → Webhooks"}
              data-testid="razorpay-webhook-secret-input"
            />
            {current?.tenant_slug && (
              <div className="mt-2 text-[11px] text-[hsl(var(--kb-muted))] bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))] rounded px-2 py-1.5">
                <div className="font-semibold mb-0.5">Webhook URL to register in Razorpay Dashboard:</div>
                <code className="font-mono text-[10px] break-all select-all" data-testid="razorpay-webhook-url">
                  {(typeof window !== "undefined" ? window.location.origin : "")}/api/payments/razorpay/webhook?tenant={current.tenant_slug}
                </code>
                <div className="mt-1 text-[10px]">
                  Subscribe to events: <code className="text-[10px]">payment.captured</code>, <code className="text-[10px]">payment.failed</code>, <code className="text-[10px]">refund.created</code>, <code className="text-[10px]">refund.processed</code>.
                </div>
              </div>
            )}
          </label>
          <button onClick={savePaymentKeys} disabled={savingPayments}
                  className="kb-btn-primary mt-4 text-sm inline-flex items-center gap-2"
                  data-testid="razorpay-save-btn">
            {savingPayments ? <Loader2 size={12} className="animate-spin" /> : null}
            Save payment keys
          </button>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Smartphone size={18} /> PWA preview</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            When a customer taps "Install app" in their browser, they'll see the icon + name below.
          </p>
          <div className="inline-flex items-center gap-4 border border-[hsl(var(--kb-border))] p-4 rounded-xl bg-[hsl(var(--kb-surface-alt))]" data-testid="branding-pwa-preview">
            <div
              className="w-14 h-14 rounded-xl overflow-hidden shadow-md"
              style={{ backgroundColor: form.background_color || "#fff" }}
            >
              {form.logo_square_url ? (
                <img src={imgUrl(form.logo_square_url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[hsl(var(--kb-muted))]">
                  <ImageIcon size={20} />
                </div>
              )}
            </div>
            <div>
              <div className="font-medium">{form.short_name || form.brand_name || "Brand"}</div>
              <div className="text-xs text-[hsl(var(--kb-muted))]">{form.tagline || "Tagline"}</div>
            </div>
          </div>
        </section>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-4 border-t border-[hsl(var(--kb-border))]">
          <button
            onClick={exportBundle}
            disabled={exporting}
            className="kb-btn-outline inline-flex items-center gap-2"
            data-testid="branding-export-btn"
            title="Download a zip with manifest, icons, theme tokens & README"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? "Packing…" : "Export white-label bundle"}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="kb-btn-primary"
            data-testid="branding-save-btn"
          >
            {saving ? "Saving…" : "Save branding"}
          </button>
        </div>

        <div className="text-[11px] text-[hsl(var(--kb-muted))] pt-2">
          Current on file: <span className="font-mono">{current?.brand_name}</span> · last updated {current?.updated_at ? new Date(current.updated_at).toLocaleString() : "never"}
        </div>
      </div>

      {/* Live storefront preview — re-mounts iframe whenever branding is saved */}
      <aside
        className="bg-white border border-[hsl(var(--kb-border))] p-4 lg:sticky lg:top-6 self-start"
        data-testid="branding-live-preview"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-medium text-sm">
            <Eye size={14} /> Live preview
          </div>
          <button
            type="button"
            onClick={() => setPreviewKey((k) => k + 1)}
            className="text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] inline-flex items-center gap-1"
            data-testid="branding-preview-refresh"
            aria-label="Refresh preview"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <p className="text-[11px] text-[hsl(var(--kb-muted))] mb-3 leading-relaxed">
          Your storefront with the currently saved branding. Saves auto-refresh this view.
        </p>
        <div className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] overflow-hidden" style={{ aspectRatio: "9 / 14" }}>
          <iframe
            key={previewKey}
            title="Storefront preview"
            src={previewSrc}
            className="w-full h-full"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            data-testid="branding-preview-iframe"
          />
        </div>
        <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-2 font-mono break-all">
          {previewSrc}
        </div>
      </aside>
      </div>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block text-sm ${className}`}>
      <div className="mb-1 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider">{label}</div>
      {children}
    </label>
  );
}

function ColorField({ label, value, presetDefault, hint, onChange, testid }) {
  // `value` is the tenant override (`""` when unset). `presetDefault` is the
  // hex value the active preset would otherwise apply. The colour-picker
  // surfaces the EFFECTIVE colour (override → fallback to preset) so the
  // swatch never goes black-by-default just because no override is saved.
  const effective = value || presetDefault || "#000000";
  const isOverride = !!value && value.toLowerCase() !== (presetDefault || "").toLowerCase();
  return (
    <Field label={label}>
      <div
        className={`flex items-center gap-2 border px-2 ${isOverride ? "border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))]"}`}
        data-testid={`branding-color-row-${testid}`}
      >
        <input
          type="color"
          value={effective}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 bg-transparent border-0 cursor-pointer"
          data-testid={`branding-color-${testid}`}
        />
        <input
          type="text"
          value={value || ""}
          placeholder={presetDefault || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 py-2 text-sm font-mono focus:outline-none bg-transparent"
          maxLength={7}
          pattern="^#[0-9a-fA-F]{6}$"
          data-testid={`branding-color-text-${testid}`}
        />
        {isOverride && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] inline-flex items-center gap-1 py-1 px-1.5"
            title="Reset to preset default"
            data-testid={`branding-color-reset-${testid}`}
          >
            <RefreshCw size={11} /> Reset
          </button>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-[hsl(var(--kb-muted))]">
        {!isOverride && presetDefault && (
          <span data-testid={`branding-color-default-badge-${testid}`}>
            Using preset default <span className="font-mono">{presetDefault}</span>
          </span>
        )}
        {isOverride && presetDefault && (
          <span>
            Override of <span className="font-mono">{presetDefault}</span>
          </span>
        )}
        {hint && <span className="ml-auto italic">{hint}</span>}
      </div>
    </Field>
  );
}

function LogoUploader({ label, value, onChange, aspectClass, hint, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.key) onChange(data.key);
      else if (data?.url) onChange(data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider mb-2">{label}</div>
      <div className={`relative ${aspectClass} border border-dashed border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] overflow-hidden`} data-testid={`branding-logo-${testid}`}>
        {value ? (
          <>
            <img src={imgUrl(value)} alt="" className="w-full h-full object-contain p-2" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center"
              data-testid={`branding-logo-${testid}-remove`}
              aria-label="Remove logo"
            >
              <X size={14} />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 flex flex-col items-center justify-center text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
            data-testid={`branding-logo-${testid}-upload`}
          >
            {uploading ? <Loader2 size={24} className="animate-spin" /> : <Upload size={24} />}
            <span className="text-xs uppercase tracking-widest mt-2">
              {uploading ? "Uploading…" : "Click to upload"}
            </span>
          </button>
        )}
      </div>
      <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-2">{hint}</p>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tenant-uploadable notification sound (FCM Android channel + iOS bundle)
// ---------------------------------------------------------------------------

function NotificationSoundUploader() {
  const [info, setInfo] = useState({ label: null, base: null, loaded: false });
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    api.get("/settings/app")
      .then(({ data }) => {
        setInfo({
          label: data.notification_sound_label || null,
          base: data.notification_sound_key
            ? data.notification_sound_key.split("/").pop().split(".")[0].toLowerCase().replace(/[^a-z0-9]/g, "_")
            : null,
          loaded: true,
        });
      })
      .catch(() => setInfo({ label: null, base: null, loaded: true }));
  }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Sound file must be ≤ 500 KB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/notification-sound", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInfo({ label: data.label, base: data.base, loaded: true });
      toast.success(`Sound saved — Android channel "${data.base}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete("/admin/notification-sound");
      setInfo({ label: null, base: null, loaded: true });
      toast.success("Removed — falls back to default tone");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div>
      <h2 className="font-display text-xl mb-2 flex items-center gap-2">
        <Sparkles size={18} /> Notification sound
      </h2>
      <p className="text-sm text-[hsl(var(--kb-muted))] mb-4 max-w-2xl">
        Custom tone played when an order push arrives in your Android app.
        Upload a short <code>.mp3</code> or <code>.wav</code> (max 500 KB, ~3-5 seconds).
        Leave blank to use the platform default. The sound only plays inside the native Android
        TWA built via PWA Builder — browser pushes use the OS default.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {info.label ? (
          <>
            <div className="flex items-center gap-2 px-3 py-2 border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] text-sm" data-testid="notification-sound-current">
              <Sparkles className="w-4 h-4 text-[hsl(var(--kb-primary))]" />
              <code className="text-xs">{info.label}</code>
              <span className="text-[10px] text-[hsl(var(--kb-muted))]">→ res/raw/{info.base}</span>
            </div>
            <button
              onClick={remove}
              className="kb-btn-outline !py-1.5 !px-3 !text-xs"
              data-testid="notification-sound-remove"
            >Remove</button>
          </>
        ) : (
          <span className="text-xs text-[hsl(var(--kb-muted))]">No custom sound — using platform default.</span>
        )}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="kb-btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
          data-testid="notification-sound-upload"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {info.label ? "Replace" : "Upload sound"}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,.mp3,.wav"
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
        data-testid="notification-sound-file-input"
      />
      {info.base && (
        <p className="mt-3 text-[11px] text-[hsl(var(--kb-muted))] leading-relaxed">
          <b>Heads up:</b> the sound only plays inside your <i>native</i> Android app after a fresh
          PWA Builder build that includes this file at <code>app/src/main/res/raw/{info.base}.mp3</code>.
          The super admin will give you an updated build bundle with this file baked in.
        </p>
      )}
    </div>
  );
}
